// MarkdownRenderer.tsx
import { Storage } from 'aws-amplify';
import React, { useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AWS_REGION } from '../../amplify/s3/storage';
import './MarkdownRenderer.scss';
import { Citation } from './assistant-models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePowerpoint, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { ScanMulti } from '../dashboard/llm/common/models';
import { CitationDetailsModal } from '../dashboard/llm/common/CitationDetailsModal';

interface MarkdownRendererProps {
    content: string;
    citations?: Citation[]; // Optional array of citation objects
    scanMulti?: ScanMulti;
}

const OFFICE_FILE_EXTENSIONS = ['.pptx', '.docx', '.xlsx', '.ppt', '.doc', '.xls'];

const isOfficeFile = (url: string): boolean => {
    const urlLower = url.toLowerCase();
    return OFFICE_FILE_EXTENSIONS.some(ext => urlLower.includes(ext));
};

const getFilenameFromS3Url = (s3Url: string): string => {
    const parts = s3Url.split('/');
    return decodeURIComponent(parts[parts.length - 1]);
};

const handleS3Click = async (event: React.MouseEvent<HTMLAnchorElement>, s3Url: string) => {
    event.preventDefault();
    event.stopPropagation();

    try {
        const [, , bucket, ...keyParts] = s3Url.split('/');
        const key = decodeURIComponent(keyParts.join('/'));
        const filename = getFilenameFromS3Url(s3Url);
        const isOffice = isOfficeFile(s3Url);

        const config: any = {
            bucket: bucket,
            region: AWS_REGION,
            contentType: 'arraybuffer',
            download: false,
            cacheControl: 'no-cache',
            customPrefix: {
                public: '',
            },
        };

        if (isOffice) {
            config.contentDisposition = `attachment; filename="${filename}"`;
            console.log(`Downloading Office file: ${filename}`);
        }

        const url = await Storage.get(key, config);

        // Use window.open for all files to maintain consistent UX
        // Content-Disposition header will force download for Office files
        window.open(url as string, '_blank');
    } catch (error) {
        console.error('Failed to get pre-signed URL:', error);
        alert('Failed to download file. Please try again.');
    }
};

const CitationBubble: React.FC<{ 
    citationNumber: string; 
    citation?: Citation; 
    href: string;
    isS3Link: boolean;
    onS3Click: (event: React.MouseEvent<HTMLAnchorElement>, s3Url: string) => void;
    scanMulti?: ScanMulti;
}> = ({ citationNumber, citation, href, isS3Link, onS3Click, scanMulti }) => {
    
    const [showPopover, setShowPopover] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedCitationNumber, setSelectedCitationNumber] = useState<string>();
    const timeoutRef = useRef<any>(null);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setShowPopover(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
        setShowPopover(false);
        }, 300); // 300ms delay before hiding
    };

    const handlePopoverMouseEnter = () => {
        if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        }
    };

    const handlePopoverMouseLeave = () => {
        setShowPopover(false);
    };

    const renderPopoverContent = () => {
        if (citation) {
            return (
                <Popover 
                    id={`citation-popover-${citationNumber}`} 
                    className="citation-popover"
                    onMouseEnter={handlePopoverMouseEnter}
                    onMouseLeave={handlePopoverMouseLeave}
                    onClick={(e: any) => {
                        if (citation.citation_id) {
                            setSelectedCitationNumber(citation.citation_id);
                            setShowModal(true);
                        } else if (citation.url && citation.url.startsWith("s3://")) {
                            handleS3Click(e, citation.url);
                        } else {
                            window.open(citation.url, "_blank");
                        }
                    }}
                >
                    <Popover.Header className="citation-popover-header">
                        Citation {citationNumber}
                    </Popover.Header>
                    <Popover.Body className="citation-popover-body">
                        {citation.title && (
                            <div className="citation-title">
                                <strong>{citation.url && citation.url.endsWith("pptx") ? <FontAwesomeIcon icon={faFilePowerpoint} /> : <FontAwesomeIcon icon={faGlobe} />} {citation.title}</strong>
                            </div>
                        )}
                    </Popover.Body>
                </Popover>
            );
        }

        // Fallback for when no citation data is provided
        return (
            <Popover id={`citation-popover-${citationNumber}`} className="citation-popover">
                <Popover.Header className="citation-popover-header">
                    Citation {citationNumber}
                </Popover.Header>
                <Popover.Body className="citation-popover-body">
                    <div className="citation-description">
                        {isS3Link ? 'Document reference' : 'External source'}
                    </div>
                    <div className="citation-link">
                        {isS3Link ? (
                            <button 
                                onClick={(e: any) => onS3Click(e, href)}
                                className="btn btn-sm btn-outline-primary mt-2"
                            >
                                View Document
                            </button>
                        ) : (
                            <a 
                                href={href} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary mt-2"
                            >
                                View Source
                            </a>
                        )}
                    </div>
                </Popover.Body>
            </Popover>
        );
    };

    return (
        <>
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                show={showPopover}
                overlay={renderPopoverContent()}
                delay={{ show: 300, hide: 150 }}
            >
                <span 
                    className="citation-bubble"
                    role="button"
                    tabIndex={0}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={(e: any) => {
                        if (citation) {
                            if (citation.citation_id) {
                                setSelectedCitationNumber(citation.citation_id);
                                setShowModal(true);
                            } else if (citation.url && citation.url.startsWith("s3://")) {
                                handleS3Click(e, citation.url);
                            } else {
                                window.open(citation.url, "_blank");
                            }
                        }
                    }}
                    aria-label={`Citation ${citationNumber}`}
                >
                    {citationNumber}
                </span>
            </OverlayTrigger>
            {
                showModal && (
                    <CitationDetailsModal citationNumber={selectedCitationNumber} scanMulti={scanMulti} show={showModal} setShow={setShowModal} />
                )
            }
        </>
    );
};

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, citations = [], scanMulti = undefined, }) => {

    // Preprocess content to convert plain citations [1] into special citation links
    const preprocessContent = (text: string): string => {
        // Match citation patterns like [1], [2], [123], [1, 2], [1,2,3], etc.
        // Use negative lookbehind/lookahead to avoid matching existing markdown links
        if (!text) {
            return text;
        }
        return text.replace(/(?<!\]\()\[(\d+(?:\s*,\s*\d+)*)\](?!\()/g, (match, numbers) => {
            // Split the numbers by comma and clean up whitespace
            const citationNumbers = numbers.split(',').map((num: string) => num.trim());
            
            // Create separate citation links for each number
            const citationLinks = citationNumbers.map((number: string) => {
                const href = `#citation-${number}`;
                return `[[${number}]](${href} "citation-${number}")`;
            });
            
            // Join them with a space (or you could use other separators like comma+space)
            return citationLinks.join(' ');
        });
    };

    const processedContent = preprocessContent(content);

    const components: Components = {
        a: ({ node, ...props }) => {
            const href = props.href || '';
            const title = props.title || '';
            const isS3Link = href.startsWith('s3://');
            const citationText = props.children?.toString() || '';
            
            // Check if this is a citation link (created by our preprocessing)
            if (title.startsWith('citation-')) {
                const citationNumber = title.replace('citation-', '');
                const citationMatch = citationText.match(/^\[(\d+)\]$/);
                
                if (citationMatch) {
                    const citationIndex = parseInt(citationNumber) - 1;
                    const citation = citations[citationIndex];

                    return (
                        <CitationBubble
                            citationNumber={citationNumber}
                            citation={citation}
                            href={href}
                            isS3Link={isS3Link}
                            onS3Click={handleS3Click}
                            scanMulti={scanMulti}
                        />
                    );
                }
            }

            // Regular link handling for non-citation links
            if (isS3Link) {
                return (
                    <a href="#" onClick={(e) => handleS3Click(e, href)}>
                        {props.children}
                    </a>
                );
            }

            if (href) {
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {props.children}
                    </a>
                );
            }
            return (
                <a href="#" target="_blank" rel="noopener noreferrer">
                    {props.children}
                </a>
            );
        },
    };

    return (
        <ReactMarkdown 
            components={components}
            remarkPlugins={[remarkGfm]}
            urlTransform={(uri) => uri}
        >
            {processedContent}
        </ReactMarkdown>
    );
};
